import React from 'react';
import { cn } from '../lib/utils';
import Button from './Button';
import { Badge } from './Badge';
import { Assessment } from '../src/types';
import { Calendar, FileText, Eye, Pencil, Archive, ArchiveRestore, Trash2 } from 'lucide-react';

interface AssessmentCardProps {
  assessment: Assessment;
  onSelect: () => void;
  onEdit: () => void;
  onArchive: () => void;
  onUnarchive?: () => void;
  onDelete: () => void;
  showingArchived?: boolean;
}

export function AssessmentCard({
  assessment,
  onSelect,
  onEdit,
  onArchive,
  onUnarchive,
  onDelete,
  showingArchived = false
}: AssessmentCardProps) {
  const createdDate = assessment.createdAt 
    ? new Date(assessment.createdAt).toLocaleDateString('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    : '';

  const yearLevelDisplay = assessment.yearLevel?.toString().toLowerCase().includes('year')
    ? assessment.yearLevel
    : `Year ${assessment.yearLevel}`;

  return (
    <div className="group bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-md transition-all duration-200 overflow-hidden">
      {/* Top accent bar */}
      <div className="h-1 bg-gradient-to-r from-primary to-primary/60" />
      
      <div className="p-5 space-y-4">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-lg font-semibold text-foreground leading-tight line-clamp-2">
              {assessment.assessmentNumber && (
                <span className="text-muted-foreground font-normal">#{assessment.assessmentNumber} </span>
              )}
              {assessment.name}
            </h3>
            <Badge variant="secondary" className="shrink-0">
              {yearLevelDisplay}
            </Badge>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5">
          {assessment.subject && (
            <Badge variant="outline" className="text-xs">
              {assessment.subject}
            </Badge>
          )}
          {assessment.year && (
            <Badge variant="outline" className="text-xs">
              {assessment.year}
            </Badge>
          )}
          {assessment.examCondition && (
            <Badge variant="outline" className="text-xs text-green-700 border-green-200 bg-green-50">
              {assessment.examCondition}
            </Badge>
          )}
          {assessment.assessmentType && (
            <Badge variant="outline" className="text-xs text-amber-700 border-amber-200 bg-amber-50">
              {assessment.assessmentType}
            </Badge>
          )}
        </div>

        {/* Question Preview */}
        {assessment.question && (
          <div className="bg-muted/50 rounded-lg p-3 border-l-2 border-primary/40">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
              Question
            </p>
            <p className="text-sm text-foreground line-clamp-2">
              {assessment.question}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          {/* Date */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            <span>Created {createdDate || 'Unknown'}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-1">
          <Button
            onClick={onSelect}
            size="sm"
            className="flex-1 gap-1.5"
          >
            <Eye className="h-4 w-4" />
            View Essays
          </Button>
          <Button
            onClick={onEdit}
            variant="outline"
            size="sm"
            className="gap-1.5"
          >
            <Pencil className="h-4 w-4" />
            <span className="hidden sm:inline">Edit</span>
          </Button>
          {showingArchived && onUnarchive ? (
            <Button
              onClick={onUnarchive}
              variant="outline"
              size="sm"
              className="gap-1.5 text-green-600 hover:text-green-700 hover:bg-green-50"
            >
              <ArchiveRestore className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={onArchive}
              variant="ghost"
              size="sm"
              className="gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <Archive className="h-4 w-4" />
            </Button>
          )}
          <Button
            onClick={onDelete}
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AssessmentCard;
